import { ComponentType } from "react"
import {
  CheckIcon,
  DocumentCheckLineIcon,
  DocumentLineIcon,
  DocumentUploadLineIcon,
  type IconProps,
  NewCommentIcon,
  OrderStatusChangeIcon,
  PaymentSuccessIcon,
  ReviewFeedbackIcon,
} from "@/modules/notifications/components/icon"

export enum NotificationTypeValue {
  PaymentSuccess = 1,
  OrderStatusChanged = 2,
  DocumentsRequested = 3,
  NewCommentFromCustomer = 4,
  NewCommentFromStaff = 5,
  DocumentReady = 6,
  ReviewFeedback = 7,
  DocumentsApproved = 10,
  DocumentsActionRequired = 11,
  DocumentsResubmitted = 12,
  DocumentsSubmitted = 13,
}

const notificationIconByValueMap: Partial<Record<NotificationTypeValue, ComponentType<IconProps>>> = {
  [NotificationTypeValue.PaymentSuccess]: PaymentSuccessIcon,
  [NotificationTypeValue.OrderStatusChanged]: OrderStatusChangeIcon,
  [NotificationTypeValue.DocumentsRequested]: DocumentUploadLineIcon,
  [NotificationTypeValue.NewCommentFromCustomer]: NewCommentIcon,
  [NotificationTypeValue.NewCommentFromStaff]: NewCommentIcon,
  [NotificationTypeValue.DocumentReady]: DocumentCheckLineIcon,
  [NotificationTypeValue.ReviewFeedback]: ReviewFeedbackIcon,
  [NotificationTypeValue.DocumentsApproved]: CheckIcon,
  [NotificationTypeValue.DocumentsActionRequired]: DocumentUploadLineIcon,
  [NotificationTypeValue.DocumentsResubmitted]: DocumentUploadLineIcon,
  [NotificationTypeValue.DocumentsSubmitted]: DocumentLineIcon,
}

const notificationIconByCodeMap: Record<string, ComponentType<IconProps>> = {
  PaymentSuccess: PaymentSuccessIcon,
  OrderStatusChanged: OrderStatusChangeIcon,
  DocumentsRequested: DocumentUploadLineIcon,
  NewCommentFromCustomer: NewCommentIcon,
  NewCommentFromStaff: NewCommentIcon,
  DocumentReady: DocumentCheckLineIcon,
  ReviewFeedback: ReviewFeedbackIcon,
  DocumentsApproved: CheckIcon,
  DocumentsActionRequired: DocumentUploadLineIcon,
  DocumentsResubmitted: DocumentUploadLineIcon,
  DocumentsSubmitted: DocumentLineIcon,
}

export const getNotificationTypeValue = (type?: string | ApiState<string> | null) => {
  return typeof type === "object" ? type?.value : undefined
}

export const getNotificationIconClassName = (type?: string | ApiState<string> | null) => {
  const value = getNotificationTypeValue(type)
  const code = typeof type === "object" ? type?.code : type

  if (
    value === NotificationTypeValue.DocumentsRequested ||
    value === NotificationTypeValue.DocumentsActionRequired ||
    value === NotificationTypeValue.DocumentsResubmitted ||
    value === NotificationTypeValue.DocumentsSubmitted ||
    code === "DocumentsRequested"
  ) {
    return "border-[#FFE2D6] bg-[#FFF2EB] text-[#F97316] dark:border-[#F973164D] dark:bg-[#F9731629] dark:text-[#FB923C]"
  }

  if (
    value === NotificationTypeValue.DocumentReady ||
    value === NotificationTypeValue.ReviewFeedback ||
    code === "DocumentReady" ||
    code === "ReviewFeedback"
  ) {
    return "border-[#E4D8FF] bg-[#F4EEFF] text-[#7C3AED] dark:border-[#7C3AED4D] dark:bg-[#7C3AED29] dark:text-[#A78BFA]"
  }

  if (value === NotificationTypeValue.DocumentsApproved || value === NotificationTypeValue.PaymentSuccess) {
    return "border-[#C4F4DD] bg-[#ECFDF5] text-[#0FA875] dark:border-[#0FA8754D] dark:bg-[#0FA87529] dark:text-[#34D399]"
  }

  return "border-[#DCE3EF] bg-[#F4F6FA] text-[#2563EB] dark:border-[#334155] dark:bg-[#1F2937] dark:text-[#93C5FD]"
}

export const getNotificationIcon = (type?: string | ApiState<string> | null) => {
  const value = getNotificationTypeValue(type)
  const code = typeof type === "object" ? type?.code : type

  return notificationIconByValueMap[value as NotificationTypeValue] || notificationIconByCodeMap[code || ""] || PaymentSuccessIcon
}
